import type { Tweaks, CreatorData, PageTweaks } from "./types";
import { STYLE_PRESETS } from "./templateData";
import { DEFAULT_BLOG_GEN } from "../../../Utils/SupabaseServices/blogGeneration";

/** Fallback tweaks (matches `DEFAULT_TWEAKS` in MasterTemplateEditor). */
export const FALLBACK_TEMPLATE_TWEAKS: Tweaks = {
  preset: "",
  palette: "Default",
  fontPair: "Default",
  aesthetic: "Editorial",
  navStyle: "standard",
  navMobileStyle: "drawer",
  footerStyle: "columns",
  contentOrder: "most-clicked",
  customColors: null,
  primaryFont: null,
  secondaryFont: null,
  axes: {
    density: null,
    radius: null,
    borders: null,
    typeWeight: null,
    imageTreatment: null,
    dropCaps: null,
  },
  pages: {
    /** Home: Hero stays; the products strip is Shop This Past Week. */
    home: {
      cfg: {
        carouselShop: {
          style: "peek",
          heading: "This Week's Content",
          carouselSlug: "auto",
          preferFeed: "this-week",
        },
        carouselProducts: {
          style: "marquee",
          heading: "Shop This Past Week",
          carouselSlug: "auto",
          preferFeed: "shop-this-past-week",
        },
      },
    },
    search: {
      cfg: {
        header: { placeholder: "Try a topic, product, or phrase…" },
      },
    },
    /** Chandler default blog index — list with thumbs + READ. */
    journal: {
      cfg: {
        JournalGrid: { style: "list", count: 24 },
        grid: { style: "list", count: 24 },
      },
    },
  },
  contentEdits: {},
  /*
   * A brand-new site starts with neither cadence picked, so the Blog panel
   * opens with nothing selected and the creator chooses. Sites that already
   * exist keep generating — `mapTemplateSettingsRow` restores the old
   * on-by-default for rows saved before this setting existed.
   */
  blogGen: {
    weeklyRoundup: false,
    aiBestPost: false,
    autoPublish: false,
    pendingBestPost: null,
    pendingWeeklyRoundup: null,
    lastRoundupAt: null,
  },
};

/** Expand `preset` → palette / fontPair / aesthetic when those fields are absent. */
export function expandTweaksFromStylePreset(tweaks: Tweaks): Tweaks {
  const presetKey = tweaks.preset?.trim();
  if (!presetKey) return tweaks;
  const bundle = STYLE_PRESETS[presetKey];
  if (!bundle) return tweaks;
  return {
    ...tweaks,
    palette: tweaks.palette || bundle.palette,
    fontPair: tweaks.fontPair || bundle.fontPair,
    aesthetic: tweaks.aesthetic || bundle.aesthetic,
  };
}

function nonEmpty(v: Record<string, unknown> | unknown[]): boolean {
  return Array.isArray(v) ? v.length > 0 : Object.keys(v).length > 0;
}

/** Map a `template_settings` row into tweaks + creator overrides. */
export function mapTemplateSettingsRow(row: {
  tweaks?: unknown;
  bio?: unknown;
  highlights?: unknown;
  links?: unknown;
  banners?: unknown;
  carousels?: unknown;
  products?: unknown;
  journal?: unknown;
  press?: unknown;
  about?: unknown;
  recent?: unknown;
}): { tweaks: Tweaks; creatorData: Partial<CreatorData> } {
  const saved = (row.tweaks ?? {}) as Partial<Tweaks>;
  let t: Tweaks = {
    ...FALLBACK_TEMPLATE_TWEAKS,
    ...saved,
    /*
     * This row predates the blog cadence setting, so it must not inherit the
     * new-site default of "off" — that would silently stop auto-generation for
     * a site already relying on it. New sites never reach here: they have no
     * row, so callers use FALLBACK_TEMPLATE_TWEAKS directly.
     */
    blogGen: saved.blogGen ?? { ...DEFAULT_BLOG_GEN },
  };
  if (t.pages) {
    const p: Record<string, PageTweaks> = { ...t.pages };
    if (p.recent != null && p.content == null) {
      p.content = p.recent;
    }
    delete p.recent;
    t = { ...t, pages: p };
  }

  const c: Partial<CreatorData> = {};
  if (row.bio && nonEmpty(row.bio as Record<string, unknown>))
    c.bio = row.bio as unknown as CreatorData["bio"];
  if (row.highlights && nonEmpty(row.highlights as unknown[]))
    c.highlights = row.highlights as unknown as CreatorData["highlights"];
  if (row.links && nonEmpty(row.links as unknown[]))
    c.links = row.links as unknown as CreatorData["links"];
  if (row.banners && nonEmpty(row.banners as Record<string, unknown>))
    c.banners = row.banners as unknown as CreatorData["banners"];
  if (row.carousels && nonEmpty(row.carousels as unknown[]))
    c.carousels = row.carousels as unknown as CreatorData["carousels"];
  if (row.products && nonEmpty(row.products as unknown[]))
    c.products = row.products as unknown as CreatorData["products"];
  if (row.press && nonEmpty(row.press as unknown[]))
    c.press = row.press as unknown as CreatorData["press"];
  if (row.about && nonEmpty(row.about as Record<string, unknown>))
    c.about = row.about as unknown as CreatorData["about"];
  if (row.recent && nonEmpty(row.recent as unknown[]))
    c.recent = row.recent as unknown as CreatorData["recent"];

  return {
    tweaks: expandTweaksFromStylePreset(t),
    creatorData: c,
  };
}

/** Theme fields used by the public bootstrap loader (kept small for sessionStorage). */
export type WebsiteLoaderTweaksCache = Pick<
  Tweaks,
  | "preset"
  | "palette"
  | "fontPair"
  | "aesthetic"
  | "navStyle"
  | "navMobileStyle"
  | "footerStyle"
  | "axes"
  | "customColors"
  | "primaryFont"
  | "secondaryFont"
> & {
  /** Compact nav Link overrides so renamed About/Content routes resolve early. */
  pageSlugEdits?: Record<string, string>;
};

/** Persist `nav.*.href` / `nav.slug.*` for public route aliasing. */
export function extractPageSlugEdits(
  contentEdits: Record<string, string> | null | undefined,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, raw] of Object.entries(contentEdits || {})) {
    const value = String(raw ?? "").trim();
    if (!value) continue;
    if (
      (key.startsWith("nav.") && key.endsWith(".href")) ||
      key.startsWith("nav.slug.")
    ) {
      out[key] = value;
    }
  }
  return out;
}

export function toWebsiteLoaderTweaksCache(tweaks: Tweaks): WebsiteLoaderTweaksCache {
  const pageSlugEdits = extractPageSlugEdits(tweaks.contentEdits);
  return {
    preset: tweaks.preset,
    palette: tweaks.palette,
    fontPair: tweaks.fontPair,
    aesthetic: tweaks.aesthetic,
    navStyle: tweaks.navStyle,
    navMobileStyle: tweaks.navMobileStyle,
    footerStyle: tweaks.footerStyle,
    axes: tweaks.axes,
    customColors: tweaks.customColors ?? null,
    primaryFont: tweaks.primaryFont ?? null,
    secondaryFont: tweaks.secondaryFont ?? null,
    ...(Object.keys(pageSlugEdits).length ? { pageSlugEdits } : {}),
  };
}

export function loaderTweaksFromCache(
  cached: WebsiteLoaderTweaksCache,
): Tweaks {
  const pageSlugEdits = cached.pageSlugEdits || {};
  return expandTweaksFromStylePreset({
    ...FALLBACK_TEMPLATE_TWEAKS,
    ...cached,
    axes: { ...FALLBACK_TEMPLATE_TWEAKS.axes, ...(cached.axes ?? {}) },
    contentEdits: {
      ...(FALLBACK_TEMPLATE_TWEAKS.contentEdits || {}),
      ...pageSlugEdits,
    },
  });
}
